const MY_TIME_ZONE = 'Asia/Kuala_Lumpur'

/** Today in Malaysia as "YYYY-MM-DD". MPG is a Malaysian business, so the
 *  production calendar is a Malaysian date wherever the browser happens to be.
 *  en-CA is ISO-shaped, which is also how the API sends CalendarDay.date, so
 *  days compare as plain strings with no parsing. */
export function todayInMalaysia(): string {
  return new Intl.DateTimeFormat('en-CA', { timeZone: MY_TIME_ZONE }).format(new Date())
}

/** The Malaysian date `days` days before today, as "YYYY-MM-DD". Report ranges
 *  seed from this rather than toISOString(), which is UTC: between 00:00 and
 *  08:00 MYT the UTC date is still yesterday, and a default range built from it
 *  silently excludes today. Arithmetic runs on the Malaysian calendar parts, so
 *  the viewer's own timezone never leaks in. */
export function daysAgoInMalaysia(days: number): string {
  const [year, month, day] = todayInMalaysia().split('-').map(Number)

  return toYmd(new Date(year!, month! - 1, day! - days))
}

/** A local Date's calendar parts as "YYYY-MM-DD" - never toISOString(), which is UTC. */
function toYmd(date: Date): string {
  return [
    date.getFullYear(),
    String(date.getMonth() + 1).padStart(2, '0'),
    String(date.getDate()).padStart(2, '0'),
  ].join('-')
}

/**
 * Calendar days from today in Malaysia to a date-only "YYYY-MM-DD": 0 is
 * today, negative is past, null when there is no date. The job pages count
 * down to a production or due date with it.
 *
 * Both ends are built from calendar parts with Date.UTC, never
 * `new Date('YYYY-MM-DD')`, so the viewer's timezone cannot move either day.
 */
export function daysUntil(value?: string | null): number | null {
  if (!value) return null

  const target = utcDay(value.slice(0, 10))
  const today = utcDay(todayInMalaysia())
  if (target === null || today === null) return null

  return Math.round((target - today) / 86_400_000)
}

function utcDay(value: string): number | null {
  const [year, month, day] = value.split('-').map(Number)

  return year && month && day ? Date.UTC(year, month - 1, day) : null
}

/**
 * First and last day of a month relative to the current Malaysian month:
 * 0 is this month, -1 last month. Report presets seed from this. Day 0 of
 * the following month is the last day of the wanted one.
 */
export function monthRangeInMalaysia(offset = 0): { from: string; to: string } {
  const [year, month] = todayInMalaysia().split('-').map(Number)

  return {
    from: toYmd(new Date(year!, month! - 1 + offset, 1)),
    to: toYmd(new Date(year!, month! + offset, 0)),
  }
}

/** 1 January of the current Malaysian year, as "YYYY-MM-DD". */
export function yearStartInMalaysia(): string {
  return `${todayInMalaysia().slice(0, 4)}-01-01`
}

/** The "YYYY-MM" month containing today in Malaysia. Calendars seed their month
 *  from this rather than toISOString(), which is UTC: between 00:00 and 08:00
 *  MYT on the 1st, UTC is still the previous month and the grid would open on
 *  a month that does not contain today. */
export function currentMonthInMalaysia(): string {
  return todayInMalaysia().slice(0, 7)
}

/**
 * A "YYYY-MM" month as "August 2026" for calendar headers. Built from a local
 * Date at the 1st of the month, so no UTC parsing can shift it.
 */
export function formatMonthYear(month: string): string {
  const [year, monthNumber] = month.split('-').map(Number)
  if (!year || !monthNumber) return month

  return new Intl.DateTimeFormat('en-GB', { month: 'long', year: 'numeric' }).format(
    new Date(year, monthNumber - 1, 1),
  )
}

/**
 * A date-only value, "YYYY-MM-DD", as DD/MM/YYYY.
 *
 * Reformatted as a string on purpose. `new Date('2026-09-02')` is parsed as UTC
 * midnight, so anyone viewing from a timezone behind UTC would see the 1st: a
 * production date is a calendar day the factory agreed to, not an instant, and
 * it must read the same everywhere.
 */
function formatDateOnly(value: string): string {
  const [year, month, day] = value.split('-')

  return year && month && day ? `${day}/${month}/${year}` : value
}

/**
 * Any date the system shows a person, as DD/MM/YYYY.
 *
 * Takes both shapes the API sends: bare dates like production_date, and full
 * timestamps. Timestamps carry an offset and so are rendered in Malaysia time
 * rather than the viewer's, which is what the browser's own toLocaleDateString
 * was doing before and why the same order read 8/24/2026 on one machine.
 */
export function formatDate(value?: string | null): string {
  if (!value) return '-'
  if (/^\d{4}-\d{2}-\d{2}$/.test(value)) return formatDateOnly(value)

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value

  return new Intl.DateTimeFormat('en-GB', {
    timeZone: MY_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
  }).format(parsed)
}

/**
 * The weekday of a date-only "YYYY-MM-DD", as "Monday". The printed job sheet
 * carries it under every stage date so the floor reads the day without
 * reaching for a calendar.
 *
 * Built from the calendar parts like formatMonthYear, never `new Date(value)`:
 * that parses as UTC midnight and names the previous day for any viewer west
 * of UTC. No timeZone is passed either - the Date is already a local-midnight
 * instant, and forcing a zone onto it shifts the day straight back.
 */
export function formatWeekday(value?: string | null): string {
  if (!value) return ''

  const [year, month, day] = value.slice(0, 10).split('-').map(Number)
  if (!year || !month || !day) return ''

  return new Intl.DateTimeFormat('en-GB', { weekday: 'long' }).format(new Date(year, month - 1, day))
}

/** As formatDate, plus the 24 hour clock, for when the time of day matters. */
export function formatDateTime(value?: string | null): string {
  if (!value) return '-'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value

  return new Intl.DateTimeFormat('en-GB', {
    timeZone: MY_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed)
}

/** As formatDateTime with a two digit year, DD/MM/YY, HH:mm, for a narrow table column. */
export function formatDateTimeShort(value?: string | null): string {
  if (!value) return '-'

  const parsed = new Date(value)
  if (Number.isNaN(parsed.getTime())) return value

  return new Intl.DateTimeFormat('en-GB', {
    timeZone: MY_TIME_ZONE,
    day: '2-digit',
    month: '2-digit',
    year: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
  }).format(parsed)
}
